import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Loader2, Pencil, Repeat, Trash2 } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import { puedeTocarTarea } from '@/lib/permisos-guard'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import { describirRecurrencia } from '@/components/tareas/forms/recurrenciaSchema'
import type { TareaFormValues } from '@/components/tareas/forms'
import {
  useActualizarTareaRecurrente,
  useAnticipacionRecurrentes,
  useEliminarTareaRecurrente,
  usePausarTareaRecurrente,
  useTareasRecurrentes,
} from '@/core/hooks/useTareasRecurrentes'
import {
  textoAnticipacion,
  type TareaRecurrente,
} from '@/core/services/tareasRecurrentesService'
import { formularioARecurrente } from '@/components/tareas/forms/formularioARecurrente'
import { TareaForm } from './TareaForm'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const formatearFecha = (iso: string | null): string => {
  if (!iso) return '—'
  const [anio, mes, dia] = iso.split('-')
  return `${dia}/${mes}/${anio}`
}

/**
 * Panel de repeticiones (ORR-304).
 *
 * Permite editar, pausar y eliminar cada repetición. Editar abre el formulario del alta con los
 * valores guardados. Pausar es lo reversible; eliminar pide confirmación y aclara que las tareas
 * ya creadas no se tocan.
 */
export function RecurrentesDialog({ open, onOpenChange }: Props) {
  const { data: recurrentes = [], isLoading } = useTareasRecurrentes()
  const { data: anticipacionDias } = useAnticipacionRecurrentes()
  const actualizar = useActualizarTareaRecurrente()
  const pausar = usePausarTareaRecurrente()
  const eliminar = useEliminarTareaRecurrente()
  const { puede, emailUsuario } = usePermissions()
  const resolveNombre = useUserNameResolver()
  const [aEditar, setAEditar] = useState<TareaRecurrente | null>(null)
  const [aEliminar, setAEliminar] = useState<TareaRecurrente | null>(null)

  const guardarEdicion = async (data: TareaFormValues) => {
    if (!aEditar) return
    try {
      await actualizar.mutateAsync({ id: aEditar.id, cambios: formularioARecurrente(data) })
      setAEditar(null)
    } catch {
      // El `onError` del hook ya avisa. El formulario queda abierto para reintentar.
    }
  }

  const confirmarEliminar = async () => {
    if (!aEliminar) return
    try {
      await eliminar.mutateAsync(aEliminar.id)
    } finally {
      setAEliminar(null)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[600px] max-h-[85vh] flex-col sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5" />
              Tareas que se repiten
            </DialogTitle>
            {/* Mientras la anticipación carga, el texto no da el número de días. */}
            <DialogDescription>
              Cada una crea sus tareas sola, en la corrida diaria de las 06:00,{' '}
              {anticipacionDias === undefined ? 'unos días antes' : textoAnticipacion(anticipacionDias)} de
              la fecha en que vencen. Pausala para que deje de generarlas sin perder la
              configuración.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 space-y-3 overflow-y-auto">
            {isLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && recurrentes.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Todavía no hay ninguna. Se crean tildando “Repetir esta tarea” al cargar una tarea
                nueva.
              </p>
            )}

            {recurrentes.map((r) => (
              <div
                key={r.id}
                className="flex items-start justify-between gap-4 rounded-lg border p-3"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{r.titulo}</p>
                    {!r.activa && <Badge variant="secondary">Pausada</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {describirRecurrencia({
                      frecuencia: r.frecuencia,
                      dia_modo: r.dia_modo,
                      dia_del_mes: r.dia_del_mes,
                      dia_semana: r.dia_semana,
                      correr_a_habil: r.correr_a_habil,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {/* El cron corre «Próxima» a la fecha siguiente apenas crea las tareas de una
                        fecha, y anota esa fecha en `ultima_generada`. Editar una repetición que
                        todavía no pasó por el cron también la anota, si el alta ya creó tareas. */}
                    {r.ultima_generada ? `Creadas hasta el ${formatearFecha(r.ultima_generada)} · ` : ''}
                    {r.activa ? `Próxima: ${formatearFecha(r.proxima_fecha)}` : 'No genera tareas'}
                    {/* El cliente o el grupo distinguen repeticiones tituladas parecido. Cliente y
                        grupo son excluyentes por CHECK, así que nunca aparecen los dos. */}
                    {r.cliente_nombre ? ` · ${r.cliente_nombre}` : ''}
                    {r.grupo_descripcion ? ` · ${r.grupo_descripcion}` : ''}
                    {r.asignado_a ? ` · ${resolveNombre(r.asignado_a)}` : ''}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    checked={r.activa}
                    disabled={!puedeTocarTarea(r, puede, emailUsuario) || pausar.isPending}
                    onCheckedChange={(activa) => pausar.mutate({ id: r.id, activa })}
                    aria-label={r.activa ? 'Pausar repetición' : 'Reactivar repetición'}
                  />
                  {puedeTocarTarea(r, puede, emailUsuario) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setAEditar(r)}
                      aria-label="Editar repetición"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {puede.eliminarTarea && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setAEliminar(r)}
                      aria-label="Eliminar repetición"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={aEditar !== null} onOpenChange={(o) => !o && setAEditar(null)}>
        {/* Un click afuera no cierra: cerrar descarta lo editado. */}
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Editar repetición</DialogTitle>
            <DialogDescription>
              Al guardar se recalcula la fecha de la próxima tarea.
            </DialogDescription>
          </DialogHeader>
          {aEditar && (
            <TareaForm
              key={aEditar.id}
              recurrente={aEditar}
              onSubmit={(data) => void guardarEdicion(data)}
              onCancel={() => setAEditar(null)}
              isLoading={actualizar.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aEliminar !== null} onOpenChange={(o) => !o && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la repetición?</AlertDialogTitle>
            <AlertDialogDescription>
              “{aEliminar?.titulo}” deja de generar tareas nuevas. Las tareas que ya creó siguen en
              el panel, con su historial intacto. Si solo querés frenarla por un tiempo, pausala en
              vez de eliminarla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void confirmarEliminar()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={eliminar.isPending}
            >
              {eliminar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
